import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, List, Typography, Pagination, Tooltip, Skeleton } from 'antd';
import { UnorderedListOutlined } from '@ant-design/icons';
import { useDashboard } from './DashboardContext';
import { mapActivityToDisplay } from './dashboardUtils';

const { Text } = Typography;

export default function RecentActivityCard({ failuresOnly = false, drillToReliability = false }) {
  const navigate = useNavigate();
  const {
    recentActivity,
    recentActivityTotal,
    recentActivityPage,
    setRecentActivityPage,
    recentActivityPageSize,
    loading,
  } = useDashboard();

  const activityDisplay = mapActivityToDisplay(recentActivity).filter(
    (item) => !failuresOnly || item.status === 'error',
  );

  const handleActivityPageChange = (page) => {
    setRecentActivityPage(page);
  };

  return (
    <Card
      className="modern-card activity-card"
      title={<span className="card-title">{failuresOnly ? 'Recent failures' : 'Recent activity'}</span>}
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 24px' }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {loading.recentActivity ? (
          <Skeleton active avatar paragraph={{ rows: 5 }} />
        ) : activityDisplay.length > 0 ? (
          <List
            itemLayout="horizontal"
            dataSource={activityDisplay}
            style={{ flex: 1, minHeight: 0 }}
            renderItem={(item, index) => {
              const isLast = index === activityDisplay.length - 1;
              return (
                <List.Item
                  className="activity-list-item"
                  style={{
                    padding: '10px 8px',
                    borderBottom: isLast ? 'none' : '1px solid var(--color-border-light)',
                    transition: 'all var(--transition-fast)',
                    borderRadius: 'var(--radius)',
                    marginBottom: isLast ? '0' : '2px',
                  }}
                >
                  <List.Item.Meta
                    avatar={(
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '36px',
                        flexShrink: 0,
                      }}
                      >
                        {item.icon}
                      </div>
                    )}
                    title={(
                      <span style={{ fontWeight: 500, fontSize: '14px', color: 'var(--color-text)' }}>
                        {item.botId ? (
                          <span
                            onClick={() => navigate(`/channels/bots/${item.botId}/activity`)}
                            className="activity-list-link"
                            role="link"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') navigate(`/channels/bots/${item.botId}/activity`);
                            }}
                          >
                            {item.title}
                          </span>
                        ) : (
                          item.title
                        )}
                        {item.userDisplay && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.userClickable) {
                                navigate(`/users/${item.userClickable}/profile`);
                              }
                            }}
                            className="activity-list-link"
                          >
                            {item.userDisplay}
                          </span>
                        )}
                        {drillToReliability && item.status === 'error' && (
                          <span
                            onClick={() => navigate('/dashboard/reliability?focus=errors')}
                            style={{ color: 'var(--color-primary)', cursor: 'pointer', marginLeft: 8, fontSize: 12 }}
                            role="link"
                            tabIndex={0}
                          >
                            View details
                          </span>
                        )}
                      </span>
                    )}
                    description={(
                      <Tooltip title={item.exactTime || item.time} placement="top">
                        <Text type="secondary" style={{ fontSize: '12px', cursor: 'help' }}>
                          {item.time}
                        </Text>
                      </Tooltip>
                    )}
                  />
                </List.Item>
              );
            }}
          />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            padding: '40px 20px',
            textAlign: 'center',
            color: 'var(--color-text-secondary)',
          }}
          >
            <UnorderedListOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
            <div style={{ fontSize: '16px', fontWeight: 500, color: 'var(--color-text)', marginBottom: '8px' }}>
              {failuresOnly ? 'No recent failures' : 'No recent activity'}
            </div>
            <div style={{ fontSize: '14px' }}>
              {failuresOnly
                ? 'Failed requests will appear here'
                : 'Your activity will appear here as you use the platform'}
            </div>
          </div>
        )}
        {!loading.recentActivity && recentActivityTotal > recentActivityPageSize && !failuresOnly && (
          <div className="activity-pagination">
            <Pagination
              current={recentActivityPage}
              total={recentActivityTotal}
              pageSize={recentActivityPageSize}
              onChange={handleActivityPageChange}
              showSizeChanger={false}
              showQuickJumper={false}
              size="small"
            />
          </div>
        )}
      </div>
    </Card>
  );
}
